import type { ReactNode } from 'react';
import { Eye, TrendingUp, Users } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { formatInt, formatNumber, formatPeriodRange } from '@/lib/format';
import type { OverviewResponse } from '@/types/api';

interface AnalyticsKpiRowProps {
  overview: OverviewResponse | undefined;
  periodDays: number;
  loading: boolean;
}

export function AnalyticsKpiRow({ overview, periodDays, loading }: AnalyticsKpiRowProps) {
  const { t } = useI18n();
  const gridClass = 'grid min-w-0 grid-cols-1 gap-3 min-[640px]:grid-cols-3';
  if (loading || !overview)
    return (
      <div className={gridClass} aria-label={t('Chargement du résumé', 'Loading summary')}>
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-[116px] rounded-2xl" />
        ))}
      </div>
    );
  const period = formatPeriodRange(periodDays);
  const periodScope =
    periodDays === 0
      ? t('Total · historique', 'Total · all time')
      : t(`Total · ${periodDays} j`, `Total · ${periodDays} d`);
  return (
    <div
      className={gridClass}
      aria-label={t('Résumé · tous les comptes', 'Summary · all accounts')}
    >
      <MetricCard
        icon={<Eye className="size-4" />}
        label={t('Vues gagnées sur la période', 'Views gained in period')}
        shortLabel={t('Vues gagnées', 'Views gained')}
        value={formatNumber(overview.totalViewsDelta)}
        exact={formatInt(overview.totalViewsDelta)}
        context={periodScope}
        title={t(`Tous les comptes · ${period}`, `All accounts · ${period}`)}
        emphasis
      />
      <MetricCard
        icon={<TrendingUp className="size-4" />}
        label={t('Abonnés actuels', 'Current followers')}
        value={formatNumber(overview.totalFollowers)}
        exact={formatInt(overview.totalFollowers)}
        context={t('Total actuel', 'Current total')}
        title={t(
          'Total des dernières mesures connues sur tous les comptes.',
          'Total of the latest known measurements across all accounts.',
        )}
      />
      <MetricCard
        icon={<Users className="size-4" />}
        label={t('Comptes actifs', 'Active accounts')}
        value={
          <>
            {formatInt(overview.activeAccounts)}
            <span className="ml-1 text-[14px] font-normal text-text-3">
              / {formatInt(overview.totalAccounts)}
            </span>
          </>
        }
        exact={t(
          `${overview.activeAccounts} comptes actifs sur ${overview.totalAccounts}`,
          `${overview.activeAccounts} active accounts out of ${overview.totalAccounts}`,
        )}
        context={t('Sur tous les comptes', 'Of all accounts')}
      />
    </div>
  );
}

function MetricCard({
  icon,
  label,
  shortLabel,
  value,
  exact,
  context,
  title,
  emphasis = false,
}: {
  icon: ReactNode;
  label: string;
  shortLabel?: string;
  value: ReactNode;
  exact: string;
  context: string;
  title?: string;
  emphasis?: boolean;
}) {
  return (
    <article
      aria-label={label}
      title={title}
      className={cn(
        'flex min-h-[116px] min-w-0 flex-col justify-between gap-3 rounded-2xl border border-line-1 bg-bg-1 p-3 shadow-card sm:p-4',
        emphasis && 'border-violet/20 bg-violet-soft/20',
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <h2 className="min-w-0 text-[11px] font-medium leading-4 text-text-2">
          {shortLabel ?? label}
        </h2>
        <span
          className={cn('shrink-0 text-text-3', emphasis && 'text-violet-deep')}
          aria-hidden="true"
        >
          {icon}
        </span>
      </div>
      <div>
        <div
          className={cn(
            'font-display truncate text-[27px] tabular-nums leading-none tracking-tight sm:text-[30px]',
            emphasis ? 'text-violet-deep' : 'text-text-1',
          )}
          title={exact}
          aria-label={exact}
        >
          {value}
        </div>
        <p className="mt-2 text-[10px] leading-4 text-text-3">{context}</p>
      </div>
    </article>
  );
}
